import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LetterOfAuthority, Customer } from '@shared/schema';
import { Plus, FileText, Filter, Trash2, Eye, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PageLayout } from '@/components/ui/page-layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';

/** apiRequest throws "<status>: <body>"; surface the server's own message when the body is JSON. */
function describeApiError(message: string): string {
  const match = /^\d{3}:\s*(\{[\s\S]*\})$/.exec(message.trim());
  if (!match) return message;
  try {
    const body = JSON.parse(match[1]) as { error?: unknown; message?: unknown };
    const detail = typeof body.error === 'string' ? body.error : body.message;
    return typeof detail === 'string' && detail ? detail : message;
  } catch {
    return message;
  }
}

export default function LoaListPage() {
  const [, navigate] = useLocation();
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [deleteLoaId, setDeleteLoaId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all LOAs
  const { data: loas, isLoading } = useQuery({
    queryKey: ['/api/letters-of-authority', { status: filter }],
    queryFn: async () => {
      let url = '/api/letters-of-authority';
      if (filter) {
        url += `?status=${filter}`;
      }
      return apiRequest('GET', url);
    },
  });

  // Fetch customer data for all LOAs
  const { data: customersResponse } = useQuery({
    queryKey: ['/api/customers?legacy=true'],
    queryFn: async () => {
      return apiRequest('GET', '/api/customers?legacy=true');
    },
  });

  // Handle both paginated and legacy responses
  const customers = Array.isArray(customersResponse)
    ? customersResponse
    : customersResponse?.data || [];

  // Delete LOA mutation
  const deleteMutation = useMutation({
    mutationFn: async (loaId: number) => {
      try {
        // First check if we're authenticated
        const authCheck = await fetch('/api/user', { credentials: 'include' });
        if (authCheck.status === 401) {
          throw new Error(
            'You must be logged in to delete a Letter of Authority. Please login first.'
          );
        }

        return apiRequest('DELETE', `/api/letters-of-authority/${loaId}`);
      } catch (error) {
        console.error('Error in LOA deletion:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Letter of Authority deleted successfully',
        variant: 'default',
      });
      // Invalidate and refetch LOAs after deletion
      queryClient.invalidateQueries({ queryKey: ['/api/letters-of-authority'] });
    },
    onError: (error: Error) => {
      // If the error mentions authentication, show login prompt
      if (error.message.includes('logged in') || error.message.includes('401')) {
        toast({
          title: 'Authentication Required',
          description:
            'Please login to delete Letters of Authority. Use the test@domelec.co.uk / test123 credentials to login.',
          variant: 'destructive',
        });
        // Redirect to login page
        navigate('/auth');
      } else {
        toast({
          title: 'Error',
          description: `Failed to delete Letter of Authority: ${error.message}`,
          variant: 'destructive',
        });
      }
    },
  });

  // Send LOA for signature mutation
  const sendLoaMutation = useMutation({
    mutationFn: async (loaId: number) => {
      try {
        // First check if we're authenticated
        const authCheck = await fetch('/api/user', { credentials: 'include' });
        if (authCheck.status === 401) {
          throw new Error(
            'You must be logged in to send a Letter of Authority. Please login first.'
          );
        }

        return apiRequest('POST', `/api/letters-of-authority/${loaId}/send`);
      } catch (error) {
        console.error('Error sending LOA for signature:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: data.emailSent
          ? 'Letter of Authority has been sent for signature successfully.'
          : 'Letter of Authority prepared, but email could not be sent. Check server logs.',
        variant: 'default',
      });
      // Invalidate and refetch LOAs after sending
      queryClient.invalidateQueries({ queryKey: ['/api/letters-of-authority'] });
    },
    onError: (error: Error) => {
      // If the error mentions authentication, show login prompt
      if (error.message.includes('logged in') || error.message.includes('401')) {
        toast({
          title: 'Authentication Required',
          description:
            'Please login to send Letters of Authority. Use the test@domelec.co.uk / test123 credentials to login.',
          variant: 'destructive',
        });
        // Redirect to login page
        navigate('/auth');
      } else if (error.message.startsWith('409')) {
        // The server refuses to resend a signed LOA; show its message and refresh the list.
        toast({
          title: 'Already signed',
          description: describeApiError(error.message),
        });
        queryClient.invalidateQueries({ queryKey: ['/api/letters-of-authority'] });
      } else {
        toast({
          title: 'Error',
          description: `Failed to send Letter of Authority: ${describeApiError(error.message)}`,
          variant: 'destructive',
        });
      }
    },
  });

  // Function to handle LOA deletion
  const handleDelete = (loaId: number) => {
    setDeleteLoaId(loaId);
  };

  // Function to confirm LOA deletion
  const confirmDelete = () => {
    if (deleteLoaId) {
      deleteMutation.mutate(deleteLoaId);
      setDeleteLoaId(null);
    }
  };

  // Function to handle LOA sending
  const handleSendLoa = (loaId: number) => {
    sendLoaMutation.mutate(loaId);
  };

  // Function to get customer name by ID
  const getCustomerName = (customerId: number): string => {
    const customer = customers?.find((c: Customer) => c.id === customerId);
    return customer?.name || 'Unknown Customer';
  };

  // Function to format date
  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <PageLayout title="Letters of Authority">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        <div className="pt-8 lg:pt-0"></div>

        <div className="mb-6 fade-in">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="accent-icon-bg">
                <FileText className="h-6 w-6 accent-icon-indigo" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-200">
                  Letters of Authority
                </h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  Manage all Letters of Authority for customer solar installation proposals
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/loa/new')} variant="domelec-blue">
                <Plus className="mr-2 h-4 w-4" />
                Create New LOA
              </Button>
            </div>
          </div>
        </div>

        <div className="card-modern p-4 mb-6 slide-up">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Filter className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
            </div>
            <h3 className="text-sm font-medium text-slate-800 dark:text-slate-200">
              Filter by Status
            </h3>
          </div>
          <div className="pill-tabs">
            <Button
              variant="ghost"
              size="sm"
              className={!filter ? 'pill-tab pill-tab-active' : 'pill-tab'}
              onClick={() => setFilter(undefined)}
            >
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={filter === 'draft' ? 'pill-tab pill-tab-active' : 'pill-tab'}
              onClick={() => setFilter('draft')}
            >
              Draft
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={filter === 'sent' ? 'pill-tab pill-tab-active' : 'pill-tab'}
              onClick={() => setFilter('sent')}
            >
              Sent
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={filter === 'signed' ? 'pill-tab pill-tab-active' : 'pill-tab'}
              onClick={() => setFilter('signed')}
            >
              Signed
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={filter === 'rejected' ? 'pill-tab pill-tab-active' : 'pill-tab'}
              onClick={() => setFilter('rejected')}
            >
              Rejected
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="card-modern p-8 fade-in">
            <div className="flex flex-col items-center justify-center h-48">
              <div className="w-12 h-12 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 dark:border-t-indigo-400 animate-spin mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Loading Letters of Authority...</p>
            </div>
          </div>
        ) : loas && loas.length > 0 ? (
          <div className="card-modern fade-in">
            {/* Desktop View (MD and larger screens) */}
            <div className="hidden md:block">
              <div className="table-container">
                <Table className="table-modern">
                  <TableHeader className="table-header">
                    <TableRow>
                      <TableHead className="table-header-cell">ID</TableHead>
                      <TableHead className="table-header-cell table-header-cell-accent">
                        Customer
                      </TableHead>
                      <TableHead className="table-header-cell">Authorized Person</TableHead>
                      <TableHead className="table-header-cell">Date</TableHead>
                      <TableHead className="table-header-cell">Status</TableHead>
                      <TableHead className="table-header-cell text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loas.map((loa: LetterOfAuthority) => (
                      <TableRow key={loa.id} className="table-row">
                        <TableCell className="table-cell-highlight">#{loa.id}</TableCell>
                        <TableCell className="table-cell">
                          {loa.customerId ? getCustomerName(loa.customerId) : 'N/A'}
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="font-medium">{loa.authorizedPersonName}</div>
                          {loa.authorizedPersonTitle && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {loa.authorizedPersonTitle}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="table-cell">
                          {formatDate(loa.dateOfSigning)}
                        </TableCell>
                        <TableCell className="table-cell">
                          {loa.status === 'draft' && (
                            <Badge className="status-badge status-badge-blue">Draft</Badge>
                          )}
                          {loa.status === 'sent' && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">
                              Sent
                            </Badge>
                          )}
                          {loa.status === 'signed' && (
                            <Badge className="status-badge-green">Signed</Badge>
                          )}
                          {loa.status === 'rejected' && (
                            <Badge className="status-badge-red">Rejected</Badge>
                          )}
                        </TableCell>
                        <TableCell className="table-actions-cell">
                          <div className="flex justify-end space-x-2">
                            {/* View Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/loa/${loa.id}`)}
                              className="border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
                            >
                              <Eye className="mr-1.5 h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                              View
                            </Button>

                            {/* Send Button - Only show for drafts */}
                            {loa.status === 'draft' && loa.customerEmail && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendLoa(loa.id)}
                                disabled={sendLoaMutation.isPending}
                                className="bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800/30 text-green-600 dark:text-green-400"
                              >
                                <Send className="mr-1.5 h-3.5 w-3.5" />
                                {sendLoaMutation.isPending ? 'Sending...' : 'Send'}
                              </Button>
                            )}

                            {/* Delete Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/30 text-red-600 dark:text-red-400"
                              onClick={() => handleDelete(loa.id)}
                            >
                              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mobile View (Small screens) */}
            <div className="grid grid-cols-1 gap-4 p-4 md:hidden">
              {loas.map((loa: LetterOfAuthority) => (
                <Card key={loa.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          LOA #{loa.id}
                        </div>
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          {loa.customerId ? getCustomerName(loa.customerId) : 'N/A'}
                        </div>
                      </div>
                      {loa.status === 'draft' && (
                        <Badge className="status-badge status-badge-blue">Draft</Badge>
                      )}
                      {loa.status === 'sent' && (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">
                          Sent
                        </Badge>
                      )}
                      {loa.status === 'signed' && (
                        <Badge className="status-badge-green">Signed</Badge>
                      )}
                      {loa.status === 'rejected' && (
                        <Badge className="status-badge-red">Rejected</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <div className="text-slate-500 dark:text-slate-400">Authorized Person</div>
                        <div className="text-slate-700 dark:text-slate-300">
                          {loa.authorizedPersonName || 'Not specified'}
                          {loa.authorizedPersonTitle && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {loa.authorizedPersonTitle}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500 dark:text-slate-400">Date</div>
                        <div className="text-slate-700 dark:text-slate-300">
                          {formatDate(loa.dateOfSigning)}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100 dark:border-slate-700/50">
                      {/* View Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/loa/${loa.id}`)}
                        className="border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
                      >
                        <Eye className="mr-1.5 h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
                        View
                      </Button>

                      {/* Send Button - Only show for drafts */}
                      {loa.status === 'draft' && loa.customerEmail && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendLoa(loa.id)}
                          disabled={sendLoaMutation.isPending}
                          className="bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800/30 text-green-600 dark:text-green-400"
                        >
                          <Send className="mr-1.5 h-3.5 w-3.5" />
                          {sendLoaMutation.isPending ? 'Sending...' : 'Send'}
                        </Button>
                      )}

                      {/* Delete Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/30 text-red-600 dark:text-red-400"
                        onClick={() => handleDelete(loa.id)}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="card-modern p-8 sm:p-12 fade-in">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-slate-800 dark:text-slate-200">
                No Letters of Authority found
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {filter
                  ? `No Letters of Authority with status "${filter}" found.`
                  : 'No Letters of Authority have been created yet.'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => navigate('/loa/new')} variant="domelec-blue">
                  <Plus className="mr-2 h-4 w-4" />
                  Create New LOA
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog for LOA Deletion */}
      <AlertDialog open={deleteLoaId !== null} onOpenChange={() => setDeleteLoaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Letter of Authority? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
